// Executive Single-Team PDF Template
// Strategic Document for USMNT/USWNT Individual Reports
// Designed for: Federation leadership, Commercial directors, Board review

export const generateExecutiveNationalTeamPDF = (data, teamId, verifiedSocialData, isDemoMode) => {
  const brand = data?.brand_metrics || {};
  const digital = data?.digital_presence || {};
  const eco = data?.economic_value || {};
  const cycle = data?.current_cycle || {};
  const historical = data?.historical_cycles || [];
  const roster = data?.roster_brand_signals || {};
  
  const isUSMNT = teamId === 'usmnt';
  const teamName = isUSMNT ? 'U.S. Men\'s National Team' : 'U.S. Women\'s National Team';
  const teamAbbrev = isUSMNT ? 'USMNT' : 'USWNT';
  const primaryColor = isUSMNT ? '#10b981' : '#3b82f6';
  const fifaRanking = data?.fifa_ranking || (isUSMNT ? 11 : 1);
  
  const formatCurrency = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(0)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value}`;
  };
  
  const formatNumber = (value) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
    return value?.toString() || '0';
  };
  
  const now = new Date();
  const dateStr = now.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const timeStr = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  
  // Get verified social data
  const getVerifiedValue = (platform) => {
    if (isDemoMode) {
      return { 
        value: digital[platform]?.followers || digital[platform]?.subscribers || 0, 
        source: 'DEMONSTRATION' 
      };
    }
    const teamData = verifiedSocialData?.teams?.[teamId]?.platforms?.[platform];
    if (teamData) {
      return { 
        value: teamData.followers || teamData.subscribers || 0, 
        source: teamData.source || 'ESTIMATED',
        handle: teamData.handle 
      };
    }
    return { 
      value: digital[platform]?.followers || digital[platform]?.subscribers || 0, 
      source: 'DEMONSTRATION' 
    };
  };
  
  const lastUpdated = verifiedSocialData?.teams?.[teamId]?.last_updated;
  const formattedLastUpdated = lastUpdated ? new Date(lastUpdated).toLocaleDateString('en-US', { 
    year: 'numeric', month: 'short', day: 'numeric' 
  }) : null;
  
  // Generate evolution data
  const generateEvolutionData = () => {
    const rows = [];
    if (historical && historical.length > 0) {
      historical.forEach(c => {
        const startYear = parseInt(c.years.split('-')[0]);
        const endYear = parseInt(c.years.split('-')[1]);
        rows.push({ year: startYear, score: c.brand_index_start, event: 'Cycle Start' });
        rows.push({ year: endYear, score: c.brand_index_end, event: c.world_cup_result });
      });
    }
    rows.push({ year: 2026, score: brand.overall_brand_score, event: 'Current Assessment' });
    return rows.sort((a, b) => a.year - b.year);
  };
  
  const evolutionData = generateEvolutionData();
  
  // Generate SVG chart
  const generateEvolutionSVG = () => {
    const width = 520;
    const height = 200;
    const padding = { top: 30, right: 50, bottom: 40, left: 50 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;
    
    const years = evolutionData.map(d => d.year);
    const scores = evolutionData.map(d => d.score);
    const minYear = Math.min(...years);
    const maxYear = Math.max(...years);
    const minScore = Math.floor((Math.min(...scores) - 10) / 10) * 10;
    const maxScore = Math.ceil((Math.max(...scores) + 10) / 10) * 10;
    
    const scaleX = (year) => padding.left + ((year - minYear) / (maxYear - minYear || 1)) * chartWidth;
    const scaleY = (score) => padding.top + chartHeight - ((score - minScore) / (maxScore - minScore || 1)) * chartHeight;
    
    const pathD = evolutionData.map((d, i) => 
      `${i === 0 ? 'M' : 'L'} ${scaleX(d.year)} ${scaleY(d.score)}`
    ).join(' ');
    
    const yTicks = [];
    for (let s = minScore; s <= maxScore; s += 10) yTicks.push(s);
    
    // Calculate YoY
    const lastTwo = evolutionData.slice(-2);
    const yoyChange = lastTwo.length >= 2 ? lastTwo[1].score - lastTwo[0].score : 0;
    const yoyPct = lastTwo.length >= 2 && lastTwo[0].score > 0 
      ? ((lastTwo[1].score - lastTwo[0].score) / lastTwo[0].score * 100).toFixed(1) : 0;
    
    return `
      <svg viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg" style="width: 100%; height: auto;">
        <defs>
          <linearGradient id="areaGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stop-color="${primaryColor}" stop-opacity="0.2"/>
            <stop offset="100%" stop-color="${primaryColor}" stop-opacity="0.02"/>
          </linearGradient>
        </defs>
        
        ${yTicks.map(s => `
          <line x1="${padding.left}" y1="${scaleY(s)}" x2="${width - padding.right}" y2="${scaleY(s)}" 
                stroke="#e5e7eb" stroke-dasharray="4,4" />
          <text x="${padding.left - 10}" y="${scaleY(s) + 4}" font-size="10" fill="#6b7280" text-anchor="end" font-family="system-ui">${s}</text>
        `).join('')}
        
        ${evolutionData.map(d => `
          <text x="${scaleX(d.year)}" y="${height - padding.bottom + 18}" font-size="10" fill="#6b7280" text-anchor="middle" font-family="system-ui">${d.year}</text>
        `).join('')}
        
        <path d="${pathD} L ${scaleX(evolutionData[evolutionData.length-1].year)} ${padding.top + chartHeight} L ${scaleX(evolutionData[0].year)} ${padding.top + chartHeight} Z" 
              fill="url(#areaGrad)" />
        
        <path d="${pathD}" fill="none" stroke="${primaryColor}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
        
        ${evolutionData.map((d, i) => {
          const isLast = i === evolutionData.length - 1;
          return `
            <circle cx="${scaleX(d.year)}" cy="${scaleY(d.score)}" r="${isLast ? 6 : 4}" fill="${isLast ? primaryColor : 'white'}" stroke="${primaryColor}" stroke-width="2" />
            ${isLast ? `
              <rect x="${scaleX(d.year) - 18}" y="${scaleY(d.score) - 28}" width="36" height="20" rx="4" fill="${primaryColor}" />
              <text x="${scaleX(d.year)}" y="${scaleY(d.score) - 14}" font-size="11" fill="white" text-anchor="middle" font-weight="600" font-family="system-ui">${d.score}</text>
            ` : ''}
          `;
        }).join('')}
        
        <text x="${width - padding.right}" y="${padding.top - 8}" font-size="10" fill="${yoyChange >= 0 ? '#059669' : '#dc2626'}" text-anchor="end" font-family="system-ui" font-weight="600">
          ${yoyChange >= 0 ? '+' : ''}${yoyPct}% YoY
        </text>
        
        <text x="12" y="${height/2}" font-size="9" fill="#9ca3af" text-anchor="middle" transform="rotate(-90, 12, ${height/2})" font-family="system-ui">Brand Index Score</text>
      </svg>
    `;
  };

  const yoyChange = eco.year_over_year_change || (isUSMNT ? 18.5 : 12.2);

  // Strategic insights based on team
  const getStrategicInsights = () => {
    if (isUSMNT) {
      return [
        {
          title: 'Primary Growth Driver: Host Country Premium',
          text: `The +${yoyChange}% year-over-year brand growth is predominantly driven by co-host status for FIFA World Cup 2026. Historical analysis of prior hosts indicates brand value premiums of 35-50% during host cycles, consistent with current trajectory.`,
          type: 'green'
        },
        {
          title: 'Upside Catalysts',
          text: 'Tournament performance remains the highest-leverage variable. A quarterfinal or deeper run in 2026 could accelerate brand value growth by an additional 20-30%. European-based roster provides strong cross-Atlantic commercial appeal.',
          type: 'green'
        },
        {
          title: 'Key Risk Factors',
          text: 'Concentration risk in European club-based player pool creates scheduling and availability exposure. Youth development pipeline depth relative to peer national teams requires monitoring.',
          type: 'amber'
        },
        {
          title: 'Commercial Opportunity',
          text: `Projected incremental value of $50-65M from 2026 host premium through merchandise revenue, broadcast rights uplift, and sponsor activation. Current digital reach of ${formatNumber(digital.total_social_reach)} provides substantial activation platform.`,
          type: 'blue'
        }
      ];
    } else {
      return [
        {
          title: 'Global Leadership Position',
          text: `World #1 FIFA ranking with overall brand score of ${brand.overall_brand_score} maintains premium market positioning. Olympic Gold (Paris 2024) reinforces elite competitive status and drives sponsorship value premium.`,
          type: 'blue'
        },
        {
          title: 'Upside Catalysts',
          text: 'Strong 2027 World Cup positioning could accelerate brand recovery. Next-generation player development (Rodman, Smith) shows promising trajectory for sustained brand relevance beyond legacy player transition.',
          type: 'green'
        },
        {
          title: 'Key Risk Factors',
          text: 'Post-World Cup 2023 (R16 exit) brand trajectory shows deceleration requiring strategic attention. Generational transition risk as legacy players (Morgan, Rapinoe) approach career endpoints represents portfolio concentration concern.',
          type: 'amber'
        },
        {
          title: 'Digital Resilience',
          text: `Digital presence metrics show steady engagement (${formatNumber(digital.total_social_reach)} total reach) independent of tournament results, indicating underlying brand resilience. However, tournament performance remains primary driver of sponsorship premium.`,
          type: 'neutral'
        }
      ];
    }
  };

  const insights = getStrategicInsights();

  return `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <title>${teamAbbrev} — Strategic Brand Intelligence Assessment</title>
      <style>
        @page { size: A4; margin: 0.9in 0.8in; }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { 
          font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', Arial, sans-serif;
          color: #1f2937; line-height: 1.65; font-size: 10px; background: white; position: relative;
        }
        .pdf-watermark {
          position: fixed; top: 50%; left: 50%;
          transform: translate(-50%, -50%) rotate(-35deg);
          font-size: 48px; font-weight: 800; color: rgba(220, 38, 38, 0.06);
          white-space: nowrap; pointer-events: none; z-index: 0; letter-spacing: 4px;
        }
        .pdf-watermark-footer {
          text-align: center; font-size: 9px; color: #999;
          border-top: 1px solid #e5e7eb; padding-top: 12px; margin-top: 30px;
        }
        
        .cover {
          height: 100vh; display: flex; flex-direction: column; justify-content: center;
          align-items: center; text-align: center; page-break-after: always;
          background: linear-gradient(145deg, #111827 0%, #1f2937 50%, #111827 100%);
          color: white; margin: -0.9in -0.8in; padding: 2.5in 1.5in; position: relative;
        }
        .cover-logo { font-size: 16px; font-weight: 600; letter-spacing: 4px; color: ${primaryColor}; margin-bottom: 80px; }
        .cover-title { font-size: 28px; font-weight: 700; margin-bottom: 12px; line-height: 1.25; letter-spacing: -0.5px; }
        .cover-subtitle { font-size: 14px; color: #9ca3af; margin-bottom: 70px; font-weight: 400; }
        .cover-badge { 
          display: inline-block; background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.15);
          padding: 10px 28px; border-radius: 4px; font-size: 10px; letter-spacing: 3px; font-weight: 600;
        }
        .cover-meta { margin-top: 60px; font-size: 10px; color: #6b7280; }
        .cover-footer { position: absolute; bottom: 50px; font-size: 9px; color: #4b5563; }
        
        .page { page-break-after: always; padding: 0; }
        .page:last-child { page-break-after: auto; }
        
        .page-header {
          display: flex; justify-content: space-between; align-items: flex-end;
          border-bottom: 1.5px solid #1f2937; padding-bottom: 10px; margin-bottom: 28px;
        }
        .page-logo { font-size: 12px; font-weight: 700; color: ${primaryColor}; letter-spacing: 1px; }
        .page-logo span { color: #1f2937; }
        .page-title { font-size: 9px; color: #6b7280; text-transform: uppercase; letter-spacing: 1.5px; font-weight: 500; }
        
        .page-footer {
          margin-top: 35px; padding-top: 12px; border-top: 1px solid #e5e7eb;
          display: flex; justify-content: space-between; font-size: 8px; color: #9ca3af;
        }
        
        .section { margin-bottom: 28px; }
        .section-title {
          font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1.5px;
          color: #1f2937; margin-bottom: 16px; padding-bottom: 8px; border-bottom: 1px solid #e5e7eb;
        }
        .section-intro { font-size: 10px; color: #4b5563; margin-bottom: 16px; line-height: 1.7; }
        
        .team-card {
          background: #f9fafb; border-radius: 6px; padding: 24px; border-left: 4px solid ${primaryColor};
        }
        .team-name { font-size: 16px; font-weight: 700; color: #1f2937; margin-bottom: 4px; }
        .team-meta { font-size: 10px; color: #6b7280; margin-bottom: 16px; }
        .team-value { font-size: 24px; font-weight: 700; color: ${primaryColor}; margin-bottom: 4px; }
        .team-label { font-size: 9px; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.5px; }
        
        .metric-box { background: #f9fafb; padding: 14px; border-radius: 5px; text-align: center; }
        .metric-value { font-size: 18px; font-weight: 700; color: #1f2937; }
        .metric-label { font-size: 8px; color: #6b7280; text-transform: uppercase; margin-top: 4px; letter-spacing: 0.3px; }
        
        .grid { display: grid; gap: 16px; }
        .grid-2 { grid-template-columns: 1fr 1fr; }
        .grid-3 { grid-template-columns: 1fr 1fr 1fr; }
        .grid-5 { grid-template-columns: repeat(5, 1fr); }
        
        .insight {
          background: #f0fdf4; border-left: 3px solid #10b981;
          padding: 14px 16px; margin-bottom: 12px; border-radius: 0 5px 5px 0;
        }
        .insight.blue { background: #eff6ff; border-left-color: #3b82f6; }
        .insight.amber { background: #fffbeb; border-left-color: #f59e0b; }
        .insight.neutral { background: #f3f4f6; border-left-color: #6b7280; }
        .insight.green { background: #f0fdf4; border-left-color: #10b981; }
        .insight-title { font-size: 10px; font-weight: 600; color: #1f2937; margin-bottom: 4px; }
        .insight-text { font-size: 9.5px; color: #4b5563; line-height: 1.6; }
        
        .table { width: 100%; border-collapse: collapse; font-size: 9px; }
        .table th, .table td { padding: 10px 12px; text-align: left; border-bottom: 1px solid #e5e7eb; }
        .table th { 
          background: #f9fafb; font-weight: 600; text-transform: uppercase; 
          font-size: 8px; letter-spacing: 0.5px; color: #6b7280;
        }
        .table tbody tr:last-child td { border-bottom: none; }
        
        .badge {
          display: inline-block; padding: 3px 8px; border-radius: 3px;
          font-size: 8px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;
        }
        .badge-verified { background: #d1fae5; color: #065f46; }
        .badge-estimated { background: #fef3c7; color: #92400e; }
        .badge-demo { background: #e5e7eb; color: #4b5563; }
        
        .chart-container { background: #fafafa; border-radius: 6px; padding: 16px; margin-bottom: 16px; }
        .chart-title { font-size: 10px; font-weight: 600; color: #374151; margin-bottom: 12px; }
        
        .footnote { font-size: 8px; color: #9ca3af; margin-top: 12px; line-height: 1.5; }
        .footnote sup { color: #6b7280; }
        
        .disclaimer {
          background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 5px;
          padding: 16px; font-size: 8.5px; color: #6b7280; line-height: 1.7;
        }
        .disclaimer strong { color: #4b5563; }
        
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .cover { background: #111827 !important; }
          .page { page-break-inside: avoid; }
        }
      </style>
    </head>
    <body>
      <div class="pdf-watermark">CONFIDENTIAL — US SOCCER PILOT</div>
      <!-- COVER PAGE -->
      <div class="cover">
        <div class="cover-logo">YBV</div>
        <h1 class="cover-title">${teamName}<br/>Strategic Brand Intelligence Assessment</h1>
        <p class="cover-subtitle">Four-Year World Cup Cycle Analysis | ${isUSMNT ? '2022–2026' : '2023–2027'}</p>
        <div class="cover-badge">CONFIDENTIAL</div>
        <div class="cover-meta">
          <div>Assessment Date: ${dateStr}</div>
          <div style="margin-top: 4px;">Report Generated: ${timeStr}</div>
        </div>
        <div class="cover-footer">YBV Brand Intelligence Platform | ISO 10668 Aligned Methodology</div>
      </div>
      
      <!-- PAGE 1: EXECUTIVE SUMMARY -->
      <div class="page">
        <div class="page-header">
          <div class="page-logo"><span>YBV</span> Intelligence</div>
          <div class="page-title">Executive Summary</div>
        </div>
        
        <div class="section">
          <div class="section-title">Estimated Brand Value Range</div>
          <div class="team-card">
            <div class="team-name">${teamName}</div>
            <div class="team-meta">FIFA World Ranking: #${fifaRanking} | ${cycle.host_status || 'CONCACAF'}</div>
            <div class="team-value">${formatCurrency(eco.range_low)} – ${formatCurrency(eco.range_high)}</div>
            <div class="team-label">Estimated Brand Value Range</div>
            <div style="margin-top: 18px; display: flex; gap: 30px; align-items: center;">
              <div>
                <span style="font-size: 22px; font-weight: 700; color: #1f2937;">${brand.overall_brand_score || 0}</span>
                <span style="font-size: 10px; color: #6b7280;"> Brand Index</span>
              </div>
              <div style="color: ${primaryColor}; font-weight: 600; font-size: 13px;">+${yoyChange}% YoY</div>
            </div>
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">Strategic Interpretation</div>
          <div class="section-intro">
            Key factors affecting the ${teamAbbrev} brand portfolio heading into the ${isUSMNT ? '2026' : '2027'} World Cup cycle.
          </div>
          
          ${insights.map(insight => `
            <div class="insight ${insight.type}">
              <div class="insight-title">${insight.title}</div>
              <div class="insight-text">${insight.text}</div>
            </div>
          `).join('')}
        </div>
        
        <div class="page-footer">
          <span>${teamAbbrev} | Strategic Brand Assessment</span>
          <span>Page 1 of 5</span>
        </div>
      </div>
      
      <!-- PAGE 2: BRAND EVOLUTION -->
      <div class="page">
        <div class="page-header">
          <div class="page-logo"><span>YBV</span> Intelligence</div>
          <div class="page-title">Brand Score Evolution</div>
        </div>
        
        <div class="section">
          <div class="section-title">${teamAbbrev}: Four-Year Cycle Trajectory</div>
          <div class="chart-container">
            ${generateEvolutionSVG()}
          </div>
          <table class="table">
            <thead>
              <tr>
                <th>Year</th>
                <th>Brand Index</th>
                <th>Key Event</th>
                <th>Δ Change</th>
              </tr>
            </thead>
            <tbody>
              ${evolutionData.map((row, i) => {
                const prev = i > 0 ? evolutionData[i-1].score : row.score;
                const change = row.score - prev;
                const changeStr = i === 0 ? '—' : (change >= 0 ? `+${change}` : `${change}`);
                return `
                  <tr>
                    <td style="font-weight: 600;">${row.year}</td>
                    <td style="font-size: 13px; font-weight: 700;">${row.score}</td>
                    <td>${row.event}</td>
                    <td style="color: ${change > 0 ? '#059669' : change < 0 ? '#dc2626' : '#6b7280'}; font-weight: 500;">${changeStr}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
        </div>
        
        <div class="section">
          <div class="section-title">Composite Brand Metrics</div>
          <div class="grid grid-5">
            <div class="metric-box"><div class="metric-value">${brand.overall_brand_score || 0}</div><div class="metric-label">Overall</div></div>
            <div class="metric-box"><div class="metric-value">${brand.brand_strength_index || 0}</div><div class="metric-label">Strength</div></div>
            <div class="metric-box"><div class="metric-value">${brand.commercial_attractiveness || 0}</div><div class="metric-label">Commercial</div></div>
            <div class="metric-box"><div class="metric-value">${brand.global_recognition || 0}</div><div class="metric-label">Global</div></div>
            <div class="metric-box"><div class="metric-value">${brand.tournament_performance_factor || 0}</div><div class="metric-label">Tournament</div></div>
          </div>
        </div>
        
        <div class="page-footer">
          <span>${teamAbbrev} | Strategic Brand Assessment</span>
          <span>Page 2 of 5</span>
        </div>
      </div>
      
      <!-- PAGE 3: DIGITAL PRESENCE -->
      <div class="page">
        <div class="page-header">
          <div class="page-logo"><span>YBV</span> Intelligence</div>
          <div class="page-title">Digital Presence Analysis</div>
        </div>
        
        <div class="section">
          <div class="section-title" style="display: flex; justify-content: space-between; align-items: center;">
            <span>Verified Social Media Signals</span>
            ${isDemoMode 
              ? '<span class="badge badge-demo">Demonstration Data</span>' 
              : '<span class="badge badge-verified">Verified Data</span>'}
          </div>
          ${!isDemoMode && formattedLastUpdated ? `<div style="font-size: 9px; color: #6b7280; margin-bottom: 16px;">Last verification: ${formattedLastUpdated}</div>` : ''}
          
          <table class="table">
            <thead>
              <tr>
                <th>Platform</th>
                <th>Followers</th>
                <th>Handle</th>
                <th>Data Source</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style="font-weight: 600;">Instagram</td>
                <td style="font-weight: 600; font-size: 12px;">${formatNumber(getVerifiedValue('instagram').value)}</td>
                <td style="color: #6b7280;">@${getVerifiedValue('instagram').handle || (isUSMNT ? 'usmnt' : 'uswnt')}</td>
                <td><span class="badge ${getVerifiedValue('instagram').source === 'VERIFIED' ? 'badge-verified' : getVerifiedValue('instagram').source === 'DEMONSTRATION' ? 'badge-demo' : 'badge-estimated'}">${getVerifiedValue('instagram').source === 'VERIFIED' ? 'Verified' : getVerifiedValue('instagram').source === 'DEMONSTRATION' ? 'Demo' : 'Estimated'}</span></td>
              </tr>
              <tr>
                <td style="font-weight: 600;">X (Twitter)</td>
                <td style="font-weight: 600; font-size: 12px;">${formatNumber(getVerifiedValue('twitter').value)}</td>
                <td style="color: #6b7280;">@${getVerifiedValue('twitter').handle || teamAbbrev}</td>
                <td><span class="badge ${getVerifiedValue('twitter').source === 'VERIFIED' ? 'badge-verified' : getVerifiedValue('twitter').source === 'DEMONSTRATION' ? 'badge-demo' : 'badge-estimated'}">${getVerifiedValue('twitter').source === 'VERIFIED' ? 'Verified' : getVerifiedValue('twitter').source === 'DEMONSTRATION' ? 'Demo' : 'Estimated'}</span></td>
              </tr>
            </tbody>
          </table>
          
          <div class="footnote">
            <sup>1</sup> <strong>Verified</strong> = Manually validated snapshot from official public account profiles.<br/>
            <sup>2</sup> Source URLs archived for audit trail.
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">Estimated Social Media Signals</div>
          <table class="table">
            <thead>
              <tr>
                <th>Platform</th>
                <th>Followers</th>
                <th>Handle</th>
                <th>Data Source</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style="font-weight: 600;">TikTok</td>
                <td style="font-weight: 600; font-size: 12px;">${formatNumber(getVerifiedValue('tiktok').value)}</td>
                <td style="color: #6b7280;">@${isUSMNT ? 'usmnt' : 'uswnt'}</td>
                <td><span class="badge badge-estimated">Estimated</span></td>
              </tr>
              <tr>
                <td style="font-weight: 600;">YouTube</td>
                <td style="font-weight: 600; font-size: 12px;">${formatNumber(getVerifiedValue('youtube').value)}</td>
                <td style="color: #6b7280;">@ussoccer</td>
                <td><span class="badge badge-estimated">Estimated</span></td>
              </tr>
            </tbody>
          </table>
          
          <div class="footnote">
            <sup>3</sup> <strong>Estimated</strong> = Derived from public signal modelling. Subject to signal variation.<br/>
            <sup>4</sup> YouTube represents shared US Soccer federation channel allocation.
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">Aggregate Digital Footprint</div>
          <div class="metric-box" style="background: linear-gradient(135deg, ${primaryColor}15 0%, ${primaryColor}05 100%); padding: 20px;">
            <div class="metric-value" style="font-size: 28px; color: ${primaryColor};">${formatNumber(digital.total_social_reach || 0)}</div>
            <div class="metric-label" style="font-size: 10px;">Total Social Media Reach</div>
          </div>
        </div>
        
        ${isDemoMode ? `
        <div class="disclaimer" style="margin-top: 16px;">
          <strong>Note:</strong> This report uses demonstration data for illustrative purposes. 
          Verified data available when demonstration mode is disabled.
        </div>
        ` : ''}
        
        <div class="page-footer">
          <span>${teamAbbrev} | Strategic Brand Assessment</span>
          <span>Page 3 of 5</span>
        </div>
      </div>
      
      <!-- PAGE 4: ROSTER BRAND ANALYSIS -->
      <div class="page">
        <div class="page-header">
          <div class="page-logo"><span>YBV</span> Intelligence</div>
          <div class="page-title">Roster Brand Analysis</div>
        </div>
        
        <div class="section">
          <div class="section-title">${teamAbbrev}: Top 5 Players by Brand Score</div>
          <table class="table">
            <thead>
              <tr>
                <th style="width: 30px;">#</th>
                <th>Player</th>
                <th>Social Following</th>
                <th>Brand Score</th>
                <th>Strategic Notes</th>
              </tr>
            </thead>
            <tbody>
              ${(roster.star_players || []).slice(0, 5).map((p, i) => {
                const notes = isUSMNT 
                  ? (p.name === 'Christian Pulisic' ? 'Premier brand asset; AC Milan platform amplifies global reach' : 
                     p.name === 'Weston McKennie' ? 'Juventus tenure provides Serie A exposure' :
                     p.name === 'Tyler Adams' ? 'Leadership positioning; captain premium potential' :
                     'Emerging profile; trajectory monitoring recommended')
                  : (p.name === 'Alex Morgan' ? 'Global ambassador; crossover media presence' : 
                     p.name === 'Megan Rapinoe' ? 'Cultural icon status; transition risk' :
                     p.name === 'Trinity Rodman' ? 'Next-generation flagship; growth trajectory strong' :
                     'Established contributor; steady positioning');
                return `
                  <tr>
                    <td style="font-weight: 600; color: ${primaryColor};">${i + 1}</td>
                    <td style="font-weight: 600;">${p.name}</td>
                    <td>${formatNumber(p.instagram_followers)}</td>
                    <td style="font-size: 14px; font-weight: 700; color: ${primaryColor};">${p.brand_score}</td>
                    <td style="font-size: 9px; color: #6b7280;">${notes}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
          <div class="footnote" style="margin-top: 12px;">
            Aggregate Roster Brand Power: ${roster.total_roster_brand_power || 'N/A'} | Average Player Score: ${roster.average_player_brand_score || 'N/A'}
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">Portfolio Implications</div>
          ${isUSMNT ? `
            <div class="insight green">
              <div class="insight-title">Concentration Analysis</div>
              <div class="insight-text">
                Christian Pulisic represents the highest individual brand value within the roster. European club platform dependency (AC Milan, Juventus, Leeds) creates both opportunity for global reach expansion and risk from scheduling/injury exposure. Recommend monitoring player-level brand metrics quarterly.
              </div>
            </div>
            <div class="insight blue">
              <div class="insight-title">Pipeline Development</div>
              <div class="insight-text">
                Next-generation players (Reyna, Musah, Weah) show promising brand trajectory but require sustained European competition exposure to realize commercial potential. Consider federation-level support for player media training and brand development.
              </div>
            </div>
          ` : `
            <div class="insight blue">
              <div class="insight-title">Generational Transition</div>
              <div class="insight-text">
                Legacy players (Morgan, Rapinoe) comprise significant portion of aggregate roster brand value. Strategic imperative to accelerate next-generation player brand development. Trinity Rodman and Sophia Smith represent primary succession candidates.
              </div>
            </div>
            <div class="insight amber">
              <div class="insight-title">Timeline Risk</div>
              <div class="insight-text">
                Brand value concentration in players approaching career endpoints creates 18-24 month transition window. Recommend proactive sponsor relationship management and graduated media exposure for emerging talent to ensure portfolio continuity.
              </div>
            </div>
          `}
        </div>
        
        <div class="page-footer">
          <span>${teamAbbrev} | Strategic Brand Assessment</span>
          <span>Page 4 of 5</span>
        </div>
      </div>
      
      <!-- PAGE 5: METHODOLOGY & DISCLAIMER -->
      <div class="page">
        <div class="page-header">
          <div class="page-logo"><span>YBV</span> Intelligence</div>
          <div class="page-title">Methodology & Disclaimer</div>
        </div>
        
        <div class="section">
          <div class="section-title">Valuation Framework</div>
          <div class="section-intro">
            YBV brand valuations employ a weighted composite model normalized across peer national team cohorts. 
            The methodology synthesizes publicly available data signals, applying volatility adjustments 
            to account for tournament-cycle variance and market condition shifts.
          </div>
          
          <table class="table">
            <thead>
              <tr>
                <th>Component</th>
                <th>Weight</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr><td style="font-weight: 600;">Digital Presence</td><td>20%</td><td>Social reach, engagement velocity, platform diversity</td></tr>
              <tr><td style="font-weight: 600;">Roster Brand Power</td><td>25%</td><td>Aggregate player scores, star concentration, depth</td></tr>
              <tr><td style="font-weight: 600;">Tournament Performance</td><td>20%</td><td>World Cup results, confederation championships, FIFA ranking</td></tr>
              <tr><td style="font-weight: 600;">Commercial Strength</td><td>20%</td><td>Kit deal value, tier-1 sponsors, broadcast rights</td></tr>
              <tr><td style="font-weight: 600;">Market Factors</td><td>15%</td><td>Host premium, domestic market size, global exposure</td></tr>
            </tbody>
          </table>
        </div>
        
        <div class="section">
          <div class="section-title">Data Hierarchy</div>
          <div class="grid grid-3">
            <div class="insight green" style="margin-bottom: 0;">
              <div class="insight-title">Priority 1: Verified</div>
              <div class="insight-text">Manual validation from official public sources with audit trail.</div>
            </div>
            <div class="insight amber" style="margin-bottom: 0;">
              <div class="insight-title">Priority 2: Estimated</div>
              <div class="insight-text">Derived from public signal modelling when direct verification unavailable.</div>
            </div>
            <div class="insight neutral" style="margin-bottom: 0;">
              <div class="insight-title">Priority 3: Modelled</div>
              <div class="insight-text">Projected values based on historical patterns and market indicators.</div>
            </div>
          </div>
        </div>
        
        <div class="disclaimer" style="margin-top: 24px;">
          <p style="margin-bottom: 10px;">
            <strong>Strategic Estimation, Not Audited Valuation:</strong> The brand valuations, projections, and index scores 
            presented are directional strategic indicators derived from publicly available data signals. They do not constitute 
            financial advice, audited appraisals, or certified valuations.
          </p>
          <p style="margin-bottom: 10px;">
            <strong>Subject to Signal Variation:</strong> All figures are subject to variation based on data source availability, 
            market conditions, tournament outcomes, and other external factors.
          </p>
          <p>
            <strong>Intended Use:</strong> Internal strategic planning, commercial negotiation support, and executive decision-making.
            Distribution beyond authorized recipients requires written consent.
          </p>
        </div>
        
        <div style="margin-top: 30px; text-align: center; color: #9ca3af; font-size: 9px;">
          <div style="margin-bottom: 8px;">
            <strong style="color: ${primaryColor}; font-size: 11px; letter-spacing: 2px;">YBV</strong>
          </div>
          <div>Brand Intelligence Platform</div>
          <div style="margin-top: 4px;">ISO 10668 Aligned | BSI-Aligned Methodology | Interbrand-Aligned Valuation</div>
        </div>
        
        <div class="page-footer">
          <span>${teamAbbrev} | Strategic Brand Assessment</span>
          <span>Page 5 of 5</span>
        </div>
      </div>
      <div class="pdf-watermark-footer">CONFIDENTIAL — US SOCCER PILOT | Generated ${new Date().toISOString()}</div>
    </body>
    </html>
  `;
};
